/**
 * Document Intelligence — Saved Documents.
 *
 * Every processed document: what it was classified as, how it went, and a way
 * back to its extracted data. Read from `document_extraction`, which the
 * pipeline writes as its final step — the list never carries a document's text,
 * so a long scan costs the same here as a short one.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Tag } from 'sharedUi/Tag';
import { Toast } from 'sharedUi/Toast';
import { Button } from 'sharedUi/Button';
import { Dropdown } from 'sharedUi/Dropdown';
import { RefreshButton } from 'sharedUi/RefreshButton';
import { formatDate } from 'sharedUi/dateUtils';
import PermissionGuard from '../../components/guards/PermissionGuard';
import PageHeader from '../../components/common/page-header';
import { Icon } from '../../components/common/icon';
import SaasDataTable, { type SaasColumn } from '../../components/common/saas-data-table';
import DeleteConfirmDialog from '../../components/dialogs/delete-confirm-dialog';
import { useDebounce } from '../../hooks/useDebounce';
import { useCanDelete } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';
import { extractErrorMessage } from '../../utils/error-handler';
import { documentExtractionsService } from '../../services/document-extractions.service';
import { documentCategoriesService } from '../../services/document-categories.service';
import {
  DOCUMENT_LANGUAGE_FILTER_OPTIONS,
  languageLabel,
  type DocumentLanguage,
  type SavedDocument,
  type SavedDocumentStatus,
} from '../../types/document-intelligence.types';
import { DOCUMENT_VIEW_BASE_PATH, UPLOAD_DOCUMENT_PATH } from './paths';

const STATUS_TAG: Record<SavedDocumentStatus, { label: string; severity: 'success' | 'info' | 'danger' }> = {
  COMPLETED: { label: 'Completed', severity: 'success' },
  PROCESSING: { label: 'Processing', severity: 'info' },
  FAILED: { label: 'Failed', severity: 'danger' },
};

/** What the pipeline stores when no category fits (UNKNOWN_CATEGORY in the backend). */
const UNKNOWN_CATEGORY = 'Unknown';

const formatSize = (bytes: number): string =>
  (bytes < 1024 * 1024 ? `${(bytes / 1024).toFixed(0)} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`);

const SavedDocuments = () => {
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);
  const canDelete = useCanDelete(JOB_NAMES.AI_MODEL_MASTER);

  const [rows, setRows] = useState<SavedDocument[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 400);
  const [first, setFirst] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState<string | null>('createdAt');
  const [sortOrder, setSortOrder] = useState<1 | -1 | null>(-1);
  const [language, setLanguage] = useState<DocumentLanguage | ''>('');
  const [category, setCategory] = useState('');
  const [toDelete, setToDelete] = useState<SavedDocument | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchRows = useCallback(async () => {
    try {
      setLoading(true);
      const res = await documentExtractionsService.list({
        search: debouncedSearch || undefined,
        page: Math.floor(first / pageSize) + 1,
        page_size: pageSize,
        sort_by: sortField || undefined,
        sort_order: sortOrder === 1 ? 'asc' : 'desc',
        language: language || undefined,
        category: category || undefined,
      });
      setRows(res.data || []);
      setTotalRecords(res.pagination?.totalItems ?? 0);
    } catch (err) {
      toast.current?.show({ severity: 'error', summary: 'Error', life: 10000,
        detail: extractErrorMessage(err, 'Failed to load documents.') });
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, first, pageSize, sortField, sortOrder, language, category]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  // Every category, inactive ones included: a document classified before its
  // category was switched off still needs to be findable. Fetched once and
  // narrowed here, so changing the language filter costs no request.
  const { data: categories } = useQuery({
    queryKey: ['document-categories', 'saved-documents-filter'],
    queryFn: () => documentCategoriesService.list({
      page: 1, page_size: 500, sort_by: 'category', sort_order: 'asc',
    }),
    select: (res) => res.data || [],
  });
  const languageOf = (c: { language?: DocumentLanguage | null }) => c.language || 'en';
  const categoryOptions = useMemo(() => [
    { label: 'All categories', value: '' },
    ...(categories || [])
      .filter((c) => !language || languageOf(c) === language)
      .map((c) => ({ label: c.category, value: c.category })),
    { label: 'Unidentified', value: UNKNOWN_CATEGORY },
  ], [categories, language]);

  const open = (row: SavedDocument) => navigate(`${DOCUMENT_VIEW_BASE_PATH}/${row._id}`);

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      setDeleting(true);
      await documentExtractionsService.delete(toDelete._id);
      toast.current?.show({ severity: 'success', summary: 'Deleted', life: 3000,
        detail: `"${toDelete.fileName}" was deleted.` });
      setToDelete(null);
      fetchRows();
    } catch (err) {
      setToDelete(null);
      toast.current?.show({ severity: 'error', summary: 'Error', life: 10000,
        detail: extractErrorMessage(err, 'Failed to delete the document.') });
    } finally {
      setDeleting(false);
    }
  };

  const columns: SaasColumn<SavedDocument>[] = useMemo(() => [
    {
      field: 'fileName',
      header: 'Document',
      sortable: true,
      hideable: false,
      body: (d) => (
        <div className="flex items-center gap-2 min-w-0">
          <Icon name="file-text" size={16} className="shrink-0 text-ink-muted" />
          <span className="truncate" title={d.fileName}>{d.fileName}</span>
        </div>
      ),
    },
    {
      field: 'category',
      header: 'Document Type',
      sortable: true,
      body: (d) => (d.category && d.category !== 'Unknown'
        ? d.category
        : <span className="text-ink-muted">Unidentified</span>),
    },
    {
      field: 'language',
      header: 'Language',
      body: (d) => languageLabel(d.language),
    },
    {
      field: 'status',
      header: 'Status',
      sortable: true,
      body: (d) => <Tag value={STATUS_TAG[d.status]?.label || d.status}
        severity={STATUS_TAG[d.status]?.severity || 'info'} />,
    },
    {
      field: 'fields',
      header: 'Fields',
      align: 'right',
      // What an operator actually scans this column for: whether anything is
      // still missing, not how many fields the category happens to have.
      body: (d) => (d.missingRequired?.length
        ? <span className="text-error">{d.fields?.length ?? 0} · {d.missingRequired.length} missing</span>
        : <span>{d.fields?.length ?? 0}</span>),
    },
    {
      field: 'ocr.pageCount',
      header: 'Pages',
      align: 'right',
      body: (d) => d.ocr?.pageCount ?? <span className="text-ink-muted">—</span>,
    },
    {
      field: 'fileSize',
      header: 'Size',
      align: 'right',
      body: (d) => formatSize(d.fileSize),
    },
    {
      field: 'createdAt',
      header: 'Processed At',
      sortable: true,
      body: (d) => formatDate(d.createdAt),
    },
    {
      field: 'actions',
      header: 'Actions',
      align: 'center',
      hideable: false,
      body: (d) => (
        <div className="flex gap-1 justify-center">
          <button aria-label="View" type="button"
            className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            onClick={() => open(d)} data-testid="SavedDocuments.Action.View">
            <i className="pi pi-eye text-sm text-green-600 dark:text-green-400" style={{ fontSize: '1.125rem' }} />
          </button>
          {canDelete && (
            <button aria-label="Delete" type="button"
              className="p-2 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              onClick={() => setToDelete(d)} data-testid="SavedDocuments.Action.Delete">
              <i className="pi pi-trash text-red-600" style={{ fontSize: '1.125rem' }} />
            </button>
          )}
        </div>
      ),
    },
  // `open` is stable enough for a column definition: it only closes over navigate.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [canDelete]);

  // "Nothing processed yet" gets its own screen; a search, language or category filter
  // that matches nothing keeps the table (and the filter) and says so.
  const nothingYet = !loading && totalRecords === 0 && !debouncedSearch && !language && !category;

  return (
    <PermissionGuard jobName={JOB_NAMES.AI_MODEL_MASTER} resourceName="Document Intelligence">
      <Toast ref={toast} position="top-right" />
      <div className="p-3" data-testid="SCR-DocumentIntelligence-SavedDocuments">
        <PageHeader
          icon="pi pi-folder"
          title="Saved Documents"
          subtitle="Documents that have been processed, and the data read out of them"
          testId="SavedDocuments.Header"
        />

        {nothingYet ? (
          <div
            className="flex flex-col items-center justify-center gap-3 rounded-xl border border-line bg-surface px-6 py-16 text-center shadow-sm"
            data-testid="SavedDocuments.Empty"
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-50 text-primary dark:bg-indigo-900/30">
              <Icon name="file-text" size={24} />
            </span>
            <div className="flex flex-col gap-1">
              <p className="text-heading font-semibold text-ink">No documents yet</p>
              <p className="text-body text-ink-muted max-w-md">
                Documents you process will appear here, with their type and extracted data.
              </p>
            </div>
            <Button label="Upload Document" icon="pi pi-upload"
              onClick={() => navigate(UPLOAD_DOCUMENT_PATH)} testId="SavedDocuments.Button.Upload" />
          </div>
        ) : (
          <div className="bg-surface rounded-xl shadow-sm border border-line overflow-hidden">
            <SaasDataTable
              tableId="saved-documents"
              columns={columns}
              data={rows}
              loading={loading}
              dataKey="_id"
              emptyMessage={language || category
                ? 'No documents match these filters'
                : 'No documents match your search'}
              first={first}
              rows={pageSize}
              totalRecords={totalRecords}
              onPage={(e) => { setFirst(e.first); setPageSize(e.rows); }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sortField={sortField || undefined}
              sortOrder={sortOrder || undefined}
              onSort={(e) => { setSortField(e.sortField); setSortOrder(e.sortOrder); setFirst(0); }}
              onFilter={() => {}}
              globalSearch={search}
              onGlobalSearchChange={(v) => { setSearch(v); setFirst(0); }}
              globalSearchPlaceholder="Search by name, type or summary..."
              rowClassName={() => 'cursor-pointer'}
              onRowClick={open}
              testId="SavedDocumentsList"
              toolbarEnd={
                <>
                  <Dropdown
                    value={language}
                    options={DOCUMENT_LANGUAGE_FILTER_OPTIONS}
                    optionLabel="label" optionValue="value"
                    style={{ minWidth: 160 }}
                    onChange={(e: { value: DocumentLanguage | '' }) => {
                      const next = e.value || '';
                      setLanguage(next);
                      // A category of the other language would match nothing.
                      const picked = categories?.find((c) => c.category === category);
                      if (next && picked && languageOf(picked) !== next) setCategory('');
                      setFirst(0);
                    }}
                    data-testid="SavedDocuments.Filter.Language"
                  />
                  <Dropdown
                    value={category}
                    options={categoryOptions}
                    optionLabel="label" optionValue="value"
                    filter={categoryOptions.length > 8}
                    style={{ minWidth: 200 }}
                    onChange={(e: { value: string }) => { setCategory(e.value || ''); setFirst(0); }}
                    data-testid="SavedDocuments.Filter.Category"
                  />
                  <span className="text-sm text-ink-muted whitespace-nowrap">
                    {totalRecords} {totalRecords === 1 ? 'document' : 'documents'}
                  </span>
                  <RefreshButton onClick={fetchRows} loading={loading} />
                  <Button label="Upload Document" icon="pi pi-upload" size="small"
                    onClick={() => navigate(UPLOAD_DOCUMENT_PATH)}
                    testId="SavedDocuments.Button.Upload" />
                </>
              }
            />
          </div>
        )}

        <DeleteConfirmDialog
          visible={!!toDelete}
          onCancel={() => setToDelete(null)}
          onDelete={confirmDelete}
          message={`Delete "${toDelete?.fileName ?? ''}" and its extracted data? The stored file is kept.`}
          loading={deleting}
          testId="SavedDocuments.Dialog.Delete"
        />
      </div>
    </PermissionGuard>
  );
};

export default SavedDocuments;
